import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import cn from 'classnames';
import { useQuill } from 'react-quilljs';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import 'quill/dist/quill.snow.css';

import { isValidQuery, newKeywordsValidator } from './QueryInput.helpers';
import { useTranslation } from 'react-i18next';
import { useMultiClickOutside } from '../../../utils/useMultiClickOutside';
import { ReactComponent as SearchIcon } from '../../SearchPage/assets/search.svg';
import { ReactComponent as CloseIcon } from '../../SearchPage/assets/close.svg';
import styles from './QueryInput.module.scss';
import { useCurrentUser } from "../../../contexts/CurrentUser";
import { ReactComponent as ManualIcon } from '../../Layout/assets/help.svg';
import { ReactComponent as LinkIcon } from '../../../assets/link.svg';

const Suggestion = {
  OR: 'OR',
  AND: 'AND',
  AND_NOT: 'AND NOT',
  NOT: 'NOT',
  STAR: '*',
  QUOTE: '""',
  BRACES: '()',
  BRACKETS: '[]',
  TOP_WORD: '<>',
  NEAR: 'NEAR',
  ONEAR: 'ONEAR',
};

const SuggestionAdvanced = {
  OR: '|',
  AND: '&',
  NOT: '!',
  STAR: '*',
  AT: '@',
  TILDE: '~',
  QUOTE: '""',
  BRACES: '()',
  BRACKET: ']',
  GREATER_THAN: '>',
  TOP_WORD: '<>',
  NEAR: 'NEAR',
  ONEAR: 'ONEAR',
}

const suggestions = [
  [
    Suggestion.AND,
    Suggestion.OR,
    Suggestion.NOT,
    Suggestion.AND_NOT,
    Suggestion.NEAR,
    Suggestion.ONEAR,
    Suggestion.BRACES,
  ],
  [
    Suggestion.QUOTE,
    Suggestion.TOP_WORD,
    Suggestion.STAR,
    Suggestion.BRACKETS,
  ],
  [
    SuggestionAdvanced.AT,
    SuggestionAdvanced.TILDE,
    SuggestionAdvanced.GREATER_THAN,
  ]
];

const suggestionsAdvanced = [
  [
    SuggestionAdvanced.OR,
    SuggestionAdvanced.AND,
    SuggestionAdvanced.NOT,
    SuggestionAdvanced.NEAR,
    SuggestionAdvanced.ONEAR,
    SuggestionAdvanced.BRACES,
  ],
  [
    SuggestionAdvanced.QUOTE,
    SuggestionAdvanced.TOP_WORD,
    SuggestionAdvanced.STAR,
    SuggestionAdvanced.BRACKET
  ]
]

export const QueryInput = 
  ({
    keywords,
    onChange = (value, isValid ) => { },
    className = '',
    isInvalid,
    disabled,
    onKeyDown = () => { },
    search = false,
    clear = () => { },
    isAdvanced = false,
    onClick = () => {},
    ref,
    isShare=false,
    onMixedNearPairChange = () => {},
  }) => {

    const { t } = useTranslation();
    const [currentUser] = useCurrentUser();

    let guideLink;

  if(
    localStorage.getItem('i18nextLng') === 'ua-UK' ||
    localStorage.getItem('i18nextLng') === 'ru-RU' ||
    localStorage.getItem('i18nextLng') === 'ru' ||
    localStorage.getItem('i18nextLng') === 'uk'
  ) {
    guideLink = 'http://docs.osavul.cloud/search-page'
  } else {
    guideLink = 'http://docs.osavul.cloud/search-page'
  }

    

    const dropdownRef = useRef(null);
    const [value, setValue] = useState(keywords ?  keywords : '');
    const [isFocus, setIsFocus] = useState(false);
    const [isSelected, setIsSelected] = useState(false);
    const [validationError, setValidationError] = useState(null);

    const { quill, quillRef } = useQuill({
      placeholder: t('Enter keywords: e.g. Zelensky AND Trump AND (negotiations OR "peace talks")'),
      modules: {
        toolbar: false,
        keyboard: {
          bindings: {
            linebreakEnter: {
              key: 'Enter',
              handler: function (range) { },
            }
          }
        }
      },
    });

    useEffect(() => {
      if (keywords === null) {
        setValue('')
        quill?.setText('')
        clear()
      }
    }, [keywords])

    const handleSuggestionClick = useCallback(
      (event) => {
        const text = quill?.getText() ?? '';
        const index = quill?.getSelection()?.index ?? text?.length;
        const suggestion = event.target.dataset.suggestion;
        const spacePrefix =
          text.at(index) === '' ||
            (text.at(index - 1) === '' && text.at(index) === '\n') ||
            (text.at(index - 1) === '\n' && text.at(index) === '\n') ||
            (text.at(index - 1) === ' ') ||
            suggestion === ',' ||
            suggestion === '>' ||
            suggestion === ']' ||
            suggestion === '@' ||
            suggestion === '*' ||
            suggestion === '~'
            ? ''
            : ' ';

        const spaceSuffix = text.at(index) === ' ' || 
          suggestion === '>' ||
          suggestion === ']' ||
          suggestion === '@' ||
          suggestion === '*' ||
          suggestion === '~' ||
          suggestion === 'NEAR' ||
          suggestion === 'ONEAR'
          ? '' 
          : ' ';

        if (suggestion === '()' || suggestion === '""' || suggestion === '[]' || suggestion === '<>') {
          quill.insertText(index, suggestion[0]);
          const sel = quill.getSelection();
          if (sel) {
            quill.insertText(sel.index + sel.length, suggestion[1]);
          }
        } else {
          quill.insertText(index, `${spacePrefix}${suggestion}${spaceSuffix}`);
        }

        quillRef.current.focus();
        // setIsFocus(false);
      },
      [quill, quillRef],
    );

    const handeClickOutside = useCallback(() => {
      setTimeout(() => setIsFocus(false));
    }, []);

    useMultiClickOutside([quillRef, dropdownRef], handeClickOutside);

    useImperativeHandle(
      ref,
      () => ({
        set: (newValue) => {
          setValue(newValue);
          if (quill && newValue !== null) {
            quill.setText(newValue);
          }
        },
        focus: () => {
          if (quill) {
            quill.focus();
          }
        },
      }),
      [quill, setValue],
    );

    useEffect(() => {
      if (disabled) {
        quill?.disable();
      } else {
        quill?.enable();
      }
    }, [quill, disabled]);

    useEffect(() => {
      if (!quill) {
        return;
      }

      const format = (text) => {
        quill.removeFormat(0, quill.getLength() - 1);

        requestAnimationFrame(() => {
          const updates = [];
          let current = '';

          for (let i = 0; i < text.length; i += 1) {
            const char = text[i];

            if (
              char === ' ' ||
              char === ',' ||
              (char === '\n' && i + 1 !== text.length)
            ) {
              updates.push({
                pointer: i,
                keyword: char,
                color: 'purple',
              });
              current = '';
              continue;
            }

            const next = text[i + 1];
            const isEnd = next === ' ' || next === undefined || next === '\n';
            const isLastComma = text[i - current.length] === ',';
            current += char;

            if (isAdvanced) {
              if (current === SuggestionAdvanced.AND &&
                next === ' ' &&
                !isLastComma &&
                isEnd) {
                updates.push({
                  pointer: i,
                  keyword: SuggestionAdvanced.AND,
                  color: 'forestgreen',
                });
                current = '';
              } else if (current === SuggestionAdvanced.OR && next === ' ' && !isLastComma && isEnd) {
                updates.push({
                  pointer: i,
                  keyword: SuggestionAdvanced.OR,
                  color: 'orange',
                });
                current = '';
              } else if (current === SuggestionAdvanced.NOT &&
                next === ' ' &&
                !isLastComma &&
                isEnd) {
                updates.push({
                  pointer: i,
                  keyword: SuggestionAdvanced.NOT,
                  color: 'crimson',
                });
                current = '';
              } else if (current === SuggestionAdvanced.NEAR &&
                !isLastComma &&
                isEnd &&
                next !== '/') {
                updates.push({
                  pointer: i - 3,
                  keyword: SuggestionAdvanced.NEAR,
                  color: '#8825EB',
                });
                current = '';
              } else if (current === SuggestionAdvanced.ONEAR &&
                !isLastComma &&
                isEnd &&
                next !== '/') {
                updates.push({
                  pointer: i - 4,
                  keyword: SuggestionAdvanced.ONEAR,
                  color: '#8825EB',
                });
                current = '';
              } else if (/^(NEAR|ONEAR)\/\d*$/.test(current) &&
                !isLastComma &&
                isEnd) {
                updates.push({
                  pointer: i - (current.length - 1),
                  keyword: current,
                  color: '#8825EB',
                });
                current = '';
              }
            } else {
              if (
                current === Suggestion.AND &&
                next === ' ' &&
                !isLastComma &&
                text[i + 2] === 'N' &&
                text[i + 3] === 'O' &&
                text[i + 4] === 'T' &&
                (text[i + 5] === ' ' ||
                  text[i + 5] === '\n' ||
                  text[i + 6] === undefined)
              ) {
                updates.push({
                  pointer: i - 2,
                  keyword: Suggestion.AND_NOT,
                  color: 'crimson',
                });
                i += 4;
                current = '';
              } else if (
                current === Suggestion.NOT &&
                next === ' ' &&
                !isLastComma &&
                isEnd
               ) {
                updates.push({
                  pointer: i - 2,
                  keyword: Suggestion.NOT,
                  color: 'crimson',
                });
                current = '';
              } else if (current === Suggestion.AND && !isLastComma && isEnd) {
                updates.push({
                  pointer: i - 2,
                  keyword: Suggestion.AND,
                  color: 'forestgreen',
                });
                current = '';
              } else if (current === Suggestion.OR && !isLastComma && isEnd) {
                updates.push({
                  pointer: i - 1,
                  keyword: Suggestion.OR,
                  color: 'orange',
                });
                current = '';
              } else if (current === Suggestion.NEAR &&
                !isLastComma &&
                isEnd &&
                next !== '/') {
                updates.push({
                  pointer: i - 3,
                  keyword: Suggestion.NEAR,
                  color: '#8825EB',
                });
                current = '';
              } else if (current === Suggestion.ONEAR &&
                !isLastComma &&
                isEnd &&
                next !== '/') {
                updates.push({
                  pointer: i - 4,
                  keyword: Suggestion.ONEAR,
                  color: '#8825EB',
                });
                current = '';
              } else if (/^(NEAR|ONEAR)\/\d*$/.test(current) &&
                !isLastComma &&
                isEnd) {
                updates.push({
                  pointer: i - (current.length - 1),
                  keyword: current,
                  color: '#8825EB',
                });
                current = '';
              } else if (isEnd) {
                current = '';
              }
            }
          }

          for (const { pointer, keyword, color } of updates) {
            if (
              keyword === Suggestion.AND_NOT ||
              keyword === Suggestion.AND ||
              keyword === Suggestion.OR ||
              keyword === Suggestion.NOT ||
              keyword === SuggestionAdvanced.NOT ||
              keyword === SuggestionAdvanced.AND ||
              keyword === SuggestionAdvanced.OR ||
              keyword === SuggestionAdvanced.NEAR ||
              keyword === SuggestionAdvanced.ONEAR ||
              keyword === ',' ||
              /^(NEAR|ONEAR)\/\d*$/.test(keyword)
            ) {
              quill.formatText(pointer, keyword.length, {
                color,
              });
            }
          }


        });
      };

      quill.setText(value);
      format(value);

      const handler = (delta) => {
        const text = quill.getText();
        onChange(
          text,
          null
        )

        requestAnimationFrame(async () => {
          let isValid = await newKeywordsValidator(text, setValidationError, t, isShare, onMixedNearPairChange).catch((error) => {
            setValidationError(t('An error occurred while validating the query'));
          });

          onChange(
            text,
            isValid,
          )
          setIsFocus(true);
        });

        for (const op of delta.ops) {
          if (op.delete || op.insert) {
            format(text);
          }
        }
      };

      const handleSelection = (e) => {
        if (e && e.length > 0) {
          setIsSelected(true)
        } else {
          setIsSelected(false)
        }
      }

      quill.on('text-change', handler);
      quill.on('selection-change', handleSelection)

      return () => {
        quill.off('text-change', handler);
        quill.off('selection-change', handleSelection)
      };
    }, [quill, value, onChange, disabled]);

    const text = quill?.getText() ?? '';
    const isInvalidQuery = isInvalid ? isInvalid : validationError !== null;
    const includesBackslash = useMemo(() => text.includes('\\'), [text]);

    if (isInvalidQuery ) {
      quillRef.current?.classList.add('ql-invalid');
    } else {
      quillRef.current?.classList.remove('ql-invalid');
    }

    return (
      <>
        <div
          translate='no'
          className={cn(styles.root, className, {
            [styles.invalid]: isInvalidQuery,

          })}
        >
          {search ? <span className={styles.iconWrapper} onClick={e => onClick(e)}><SearchIcon className={styles.icon}/></span> : ''}
          <div
            ref={quillRef}
            className={search ? `${styles.input} ${styles.search}` : styles.input}
            onFocus={(e) => {
              setIsFocus(true)
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && event.shiftKey === false) {
                onKeyDown(event)
                setIsFocus(false)
              }
              if (isSelected === true) {
                setIsSelected(false)
              }
            }}
          />
          {keywords?.trim() ? <CloseIcon
            className={search ? `${styles.searchClose} ${disabled ? styles.disabled : ''} ` : `${styles.close} ${disabled ? styles.disabled : ''}`}
            onClick={() => {
              if (disabled) return;
              quill?.setText('')
              clear()
            }}
          /> : ''}
          <div
            ref={dropdownRef}
            className={cn(styles.dropdown, {
              [styles.visible]: text.length > 0 && !disabled && (isInvalidQuery || isFocus),
              [styles.invalid]: isInvalidQuery,
            })}
          >
            <div className={styles.heading}>
              { validationError !== null ? (
                <div className={styles.errorMessage}>
                { validationError}
                </div>
              ) : ''}
              <span>{t('Operators')}</span>
              <span>({t('e.g. Zelensky, president AND terrorism AND NOT Biden, Putin',)}). </span>
            </div>
            <div className={styles.suggestionsContainer}>
              <div className={styles.suggestions}>
                {!isAdvanced ? suggestions.map((suggestionGroup, i) => (
                  <div key={`operator_group_${i}`} className={styles.operatorGroup}>
                    {suggestionGroup.map(suggestion => (
                      <button
                        key={suggestion}
                        disabled={
                          (
                            isSelected && !(suggestion === '()' || suggestion === '""' || suggestion === '<>')
                          ) ? true : false
                        }
                        data-suggestion={suggestion}
                        type="button"
                        className={styles.suggestion}
                        onClick={handleSuggestionClick}
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                )) : suggestionsAdvanced.map((suggestionGroup, i) => (
                  <div key={`operator_group_${i}`} className={styles.operatorGroup}>
                    {suggestionGroup.map(suggestion => (
                      <button
                        key={suggestion}
                        disabled={
                          (
                            isSelected && !(suggestion === '()' || suggestion === '""' || suggestion === '<>')
                          ) ? true : false
                        }
                        data-suggestion={suggestion}
                        type="button"
                        className={styles.suggestion}
                        onClick={handleSuggestionClick}
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
              <button
                className={styles.searchGuideButton}
                type="button"
                onClick={(e) => {
                    e.preventDefault();
                    window.open(guideLink, '_blank', 'noreferrer')
                  }}
                >
                <div className={styles.iconWrapper}>
                  <ManualIcon className={styles.manualIcon} />
                </div>
                {t('Search guide')}
                <div className={styles.iconWrapper}>
                  <LinkIcon className={styles.linkIcon} />
                </div>
              </button>

            </div>
            
          </div>
        </div>
      </>
    );
  }
